/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useState } from 'react';

import { Center, Menu } from '@mantine/core';
import {
  ResourceContextMenuProps,
  ResourceContextMenuInternalProps,
  BaseContextMenuResource,
} from './types';
import InnerMenu from './InnerMenu';
import { ResourceAction } from '@app/types/blackroom-app';

const RESOURCE_CONTEXT_MENU_WIDTH = 210;
const DEFAULT_ALLOWED_ACTIONS: readonly ResourceAction[] = [
  'share',
  'move',
  'rename',
  'delete',
];

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourceContextMenu<
  const TActions extends readonly ResourceAction[] = readonly ResourceAction[],
>(props: ResourceContextMenuProps<TActions>) {
  /* ********************************** PROPS ********************************* */

  const {
    resources,
    renderWithoutTarget,
    target,
    allowedActions = DEFAULT_ALLOWED_ACTIONS,
    ...restProps
  } = props;

  /* ********************************** HOOKS ********************************* */

  // State
  const [opened, setOpened] = useState(false);

  /* ***************************** LOCAL VARIABLES **************************** */

  const internalProps: ResourceContextMenuInternalProps = {
    ...restProps,
    resources: (resources ?? []) as BaseContextMenuResource[],
    allowedActions,
    renderWithoutTarget,
  };

  /* ******************************** RENDERING ******************************* */

  if (allowedActions.length === 0) {
    return null;
  }

  if (renderWithoutTarget || !target) {
    return (
      <Center w={RESOURCE_CONTEXT_MENU_WIDTH}>
        <InnerMenu {...internalProps} />
      </Center>
    );
  }
  return (
    <Menu
      shadow="md"
      width={RESOURCE_CONTEXT_MENU_WIDTH}
      withinPortal
      {...restProps}
      opened={opened}
      onChange={setOpened}
    >
      <Menu.Target>{target}</Menu.Target>
      <Menu.Dropdown>
        <InnerMenu {...internalProps} />
      </Menu.Dropdown>
    </Menu>
  );
}

export default ResourceContextMenu;
